// Recherche d'un chantier par son numéro, son client, son objet ou sa ville.
//
// On ne retient pas « CH-2026-0057 » : on retient « la mairie de Pierrerue »,
// « les gouttières de Chemin », « 0057 ». La recherche doit répondre à tout
// cela, et pardonner les accents comme celle des clients.
//
// Les règles sont les mêmes qu'au carnet d'adresses : chaque mot tapé doit se
// retrouver quelque part, dans n'importe quel ordre, et le plus probable
// remonte — un chantier en cours passe devant un chantier terminé, à égalité
// de pertinence, parce qu'on facture rarement ce qui est archivé.
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

import { motsRecherche, normaliserTexte } from './recherche-client.js';

/** Le strict nécessaire pour retrouver un chantier. */
export interface ChantierRecherchable {
  id: string;
  numero: string;
  libelle: string;
  /** Nom du client, quand le chantier en a un. */
  client: string | null;
  ville: string | null;
  statut: string;
}

/** Ces chantiers-là descendent en fin de liste : on ne facture plus dessus. */
const STATUTS_DORMANTS = new Set(['termine', 'receptionne', 'annule', 'archive']);

interface ChantierIndexe {
  chantier: ChantierRecherchable;
  numero: string;
  /** Client et libellé réunis : ce par quoi on désigne un chantier. */
  principal: string;
  motsPrincipal: string[];
  ville: string;
  /** Les seuls chiffres du numéro : « 0057 » doit trouver CH-2026-0057. */
  chiffres: string;
}

function indexer(c: ChantierRecherchable): ChantierIndexe {
  const principal = normaliserTexte(`${c.client ?? ''} ${c.libelle}`);
  return {
    chantier: c,
    numero: normaliserTexte(c.numero),
    principal,
    motsPrincipal: principal === '' ? [] : principal.split(' '),
    ville: normaliserTexte(c.ville),
    chiffres: c.numero.replace(/\D+/g, ''),
  };
}

/** Ce que vaut un mot pour ce chantier : 0 s'il ne le retrouve nulle part. */
function scoreMot(i: ChantierIndexe, mot: string): number {
  if (i.numero.startsWith(mot)) return 100;
  if (i.principal.startsWith(mot)) return 90;
  if (i.motsPrincipal.some((m) => m.startsWith(mot))) return 60;
  if (i.ville.startsWith(mot)) return 40;
  if (i.principal.includes(mot)) return 30;
  // « 0057 », « 57 » : on cherche aussi dans les chiffres du numéro.
  if (/^\d+$/.test(mot) && i.chiffres.includes(mot)) return 25;
  if (i.ville.includes(mot)) return 10;
  return 0;
}

/** Vaut-il encore la peine d'être proposé en premier ? */
export function chantierActif(statut: string): boolean {
  return !STATUTS_DORMANTS.has(statut);
}

/**
 * Les chantiers qui correspondent, les plus probables en tête.
 *
 * Une recherche vide rend TOUT LE MONDE, les chantiers vivants d'abord et le
 * numéro le plus récent en tête : c'est presque toujours celui qu'on cherche.
 */
export function chercherChantiers(
  chantiers: readonly ChantierRecherchable[],
  saisie: string,
): ChantierRecherchable[] {
  const mots = motsRecherche(saisie);

  const parDefaut = (a: ChantierRecherchable, b: ChantierRecherchable): number => {
    const va = chantierActif(a.statut) ? 0 : 1;
    const vb = chantierActif(b.statut) ? 0 : 1;
    return va - vb || b.numero.localeCompare(a.numero, 'fr');
  };

  if (mots.length === 0) return chantiers.slice().sort(parDefaut);

  const out: { c: ChantierRecherchable; score: number }[] = [];
  for (const c of chantiers) {
    const i = indexer(c);
    let score = 0;
    let complet = true;
    for (const mot of mots) {
      const s = scoreMot(i, mot);
      if (s === 0) {
        complet = false;
        break;
      }
      score += s;
    }
    if (complet) out.push({ c, score });
  }

  return out
    .sort((a, b) => b.score - a.score || parDefaut(a.c, b.c))
    .map((r) => r.c);
}
